import { Prop, PropType } from "../../models/Prop";
import usePropHelper from "../../hooks/PropHelper";
import { useMemo, useState } from "react";
import PiPopoverMenu from "../../components/PiPopoverMenu";
import PiMenuButton from "../menu/PiMenuButton";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePropPreset,
  selectAllPropPresets,
  selectSelectedPropPresetId,
  setSelectedPropPreset,
} from "../../app/slices/TrainingSlice";
import classNames from "classnames";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import useProjectSafety from "../../hooks/ProjectSafety.hook";
import PropDeletionBlockedDialog from "./PropDeletionBlockedDialog";
import type { PropReference } from "../../helpers/PropReferences";

/** One prop in the Props column: the same compact row as an actor, with its type as a quiet second line. */
export default function PropCard(props: { prop: Prop }) {
  const { prop } = { ...props };
  const { getPropDTO } = usePropHelper();
  const dto = useMemo(() => getPropDTO(prop.propId), [prop.propId, getPropDTO]);
  const { t } = useTranslation();
  const selectedPropId = useSelector(selectSelectedPropPresetId);
  const allProps = useSelector(selectAllPropPresets);
  const isSelected = selectedPropId === prop.id;
  const dispatch = useDispatch();
  const [blockedReferences, setBlockedReferences] = useState<PropReference[]>([]);

  const { getPropReferencesInProject } = useProjectSafety();
  const onClick = () => {
    dispatch(setSelectedPropPreset(prop.id));
  };

  const onDelete = () => {
    const references = getPropReferencesInProject(prop);
    if (references.length) setBlockedReferences(references);
    else dispatch(deletePropPreset(prop.id));
  };

  // Ordinary (library) props get no type line; only the special kinds are worth calling out.
  const typeLabel = ((): string | undefined => {
    if (prop.link !== undefined) {
      const parent = allProps.find((candidate) => candidate.id === prop.link?.parentId);
      return parent
        ? String(t("prop_list.part_of", { name: parent.name }))
        : String(t("prop_list.linked"));
    }
    if (prop.propType === PropType.Situation) return String(t("prop_list.situation"));
    if (prop.propType === PropType.Environment) return String(t("prop_list.environment"));
    return undefined;
  })();

  const menu = (
    <div
      className={classNames("flex flex-none items-center", {
        "invisible group-hover:visible group-focus-within:visible": !isSelected,
      })}
    >
      <PiPopoverMenu>
        <PiMenuButton
          text={t("delete")}
          onClick={onDelete}
          disabled={prop.link !== undefined}
        />
        <></>
      </PiPopoverMenu>
    </div>
  );

  const thumbnail = (
    <span className="flex h-[28px] w-[28px] flex-none items-center justify-center overflow-hidden rounded-[5px] bg-creator-surfaceHover text-[16px] text-creator-text-sub">
      {dto?.propContent.downloadUri ? (
        <img className="h-full w-full object-contain p-[2px] box-border" src={dto.propContent.downloadUri} alt="" />
      ) : (
        <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
      )}
    </span>
  );

  const confirmationDialog = (
    <PropDeletionBlockedDialog
      references={blockedReferences}
      onClose={() => setBlockedReferences([])}
    />
  );

  return (
    <div
      className={classNames(
        "group flex w-full flex-none cursor-pointer items-center gap-[12px] rounded-[6px] px-[8px] text-[14px] select-none",
        typeLabel ? "h-[48px]" : "h-[40px]",
        isSelected
          ? "bg-creator-primary-washed text-creator-primary"
          : "text-creator-text-sub hover:bg-creator-surfaceHover"
      )}
      onClick={onClick}
      data-testid={`prop-card-${prop.id}`}
    >
      {thumbnail}
      <div className="flex min-w-0 flex-1 flex-col leading-[1.25]">
        <span className={classNames("truncate", isSelected && "font-semibold")}>
          {prop.name}
        </span>
        {typeLabel && (
          <span className="truncate text-[12px] font-normal text-creator-text-sub" data-testid="prop-card-type">
            {typeLabel}
          </span>
        )}
      </div>
      {menu}
      {confirmationDialog}
    </div>
  );
}
